import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Boxes, Truck, PackageCheck, AlertTriangle, RotateCcw, CalendarClock, ArrowRight, Wallet } from "lucide-react";
import api, { inr, fmtDate } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { FullScreenLoader } from "../../context/AuthContext";

const CARDS = [
  { key: "total", label: "Total Shipments", icon: Boxes, cls: "bg-shipany-blue" },
  { key: "today", label: "Today", icon: CalendarClock, cls: "bg-slate-600" },
  { key: "in_transit", label: "In Transit", icon: Truck, cls: "bg-blue-500" },
  { key: "out_for_delivery", label: "Out for Delivery", icon: PackageCheck, cls: "bg-violet-500" },
  { key: "delivered", label: "Delivered", icon: PackageCheck, cls: "bg-emerald-500" },
  { key: "ndr", label: "NDR", icon: AlertTriangle, cls: "bg-orange-500" },
  { key: "rto", label: "RTO", icon: RotateCcw, cls: "bg-red-500" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <FullScreenLoader label="Loading dashboard" />;

  return (
    <div className="space-y-8" data-testid="customer-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Dashboard</h1>
          <p className="text-sm text-slate-500">Your shipping operation at a glance.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/app/create-shipment" data-testid="dashboard-create-shipment-btn"
            className="flex items-center gap-2 rounded-xl bg-shipany-blue px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0648B3] glow-blue">
            Create Shipment <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="dashboard-stats">
        {CARDS.map((c) => (
          <div key={c.key} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`stat-card-${c.key}`}>
            <span className={`flex h-8 w-8 items-center justify-center rounded-lg ${c.cls} text-white`}><c.icon className="h-4 w-4" /></span>
            <p className="mono mt-3 text-2xl font-bold text-shipany-navy">{data.stats[c.key]}</p>
            <p className="text-xs font-medium text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white lg:col-span-3" data-testid="recent-shipments-card">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Recent Shipments</h2>
            <Link to="/app/shipments" className="text-sm font-semibold text-shipany-blue hover:underline">View all</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">AWB</th><th className="px-4 py-3">Courier</th><th className="px-4 py-3">Route</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recent.length === 0 && <tr><td colSpan={6} className="px-6 py-10 text-center text-slate-400">No shipments yet. Create your first one!</td></tr>}
                {data.recent.map((s) => (
                  <tr key={s.shipment_id} className="border-t border-slate-100 hover:bg-slate-50/60">
                    <td className="mono px-6 py-3.5 font-semibold text-shipany-blue"><Link to={`/track/${s.awb}`}>{s.awb}</Link></td>
                    <td className="px-4 py-3.5">{s.courier_name}</td>
                    <td className="px-4 py-3.5 text-slate-500">{s.origin_city} → {s.destination_city}</td>
                    <td className="mono px-4 py-3.5 font-semibold">{inr(s.price)}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3.5 text-slate-500">{fmtDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex flex-col gap-6">
          <div className="rounded-2xl bg-shipany-navy p-6 text-white" data-testid="wallet-card">
            <Wallet className="h-6 w-6 text-shipany-cyan" />
            <p className="mt-4 text-xs uppercase tracking-widest text-slate-400">Wallet Balance</p>
            <p className="mono mt-1 text-3xl font-bold" data-testid="dashboard-wallet-balance">{inr(data.wallet_balance)}</p>
            <Link to="/app/wallet" data-testid="wallet-add-funds-link" className="mt-4 block rounded-lg bg-shipany-cyan py-2.5 text-center text-sm font-bold text-shipany-navy transition-colors hover:bg-[#0EB4DE]">
              Add Funds
            </Link>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="font-display text-sm font-bold text-shipany-navy">Quick actions</h3>
            <div className="mt-3 space-y-2">
              <Link to="/app/rate-calculator" className="block rounded-lg bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Calculate rates</Link>
              <Link to="/app/track" className="block rounded-lg bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Track a shipment</Link>
              <Link to="/app/warehouses" className="block rounded-lg bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100">Manage warehouses</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
